import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, DollarSign, Users, Wallet, Loader2, ChevronDown, ChevronUp, BadgeCheck, Crown, Search, Mail } from 'lucide-react'

interface AdminStats {
  wallets: any[]
  totalReferrals: number
  pendingWithdrawals: any[]
  totalTokensPaid: number
  totalNairaPaid: number
}

function AdminAffiliates() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [downlines, setDownlines] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingDownlines, setLoadingDownlines] = useState(false)
  const [expandedAff, setExpandedAff] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'tracking' | 'wallets' | 'withdrawals'>('tracking')

  useEffect(() => { fetchStats() }, [])

  const fetchStats = async () => {
    try {
      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) return

      const res = await fetch('/api/affiliate/admin-stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) setStats(await res.json())
    } catch (err) {
      console.error('Failed to fetch affiliate stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDownlines = async () => {
    setLoadingDownlines(true)
    try {
      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) return

      const res = await fetch('/api/affiliate/admin-downlines', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setDownlines(data.affiliates || [])
      }
    } catch (err) {
      console.error('Failed to fetch downlines:', err)
    } finally {
      setLoadingDownlines(false)
    }
  }

  useEffect(() => { fetchDownlines() }, [])

  const processWithdrawal = async (id: string, action: 'approved' | 'rejected' | 'paid') => {
    setProcessingId(id)
    try {
      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token

      await fetch('/api/affiliate/admin-process-withdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ withdrawal_id: id, action })
      })
      await fetchStats()
    } catch (err) {
      console.error('Failed to process withdrawal:', err)
    } finally {
      setProcessingId(null)
    }
  }

  const filteredAffiliates = downlines.filter((aff) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      (aff.full_name || '').toLowerCase().includes(q) ||
      (aff.email || '').toLowerCase().includes(q) ||
      (aff.referral_code || '').toLowerCase().includes(q)
    )
  })

  const totalPaidUsers = downlines.reduce((sum, aff) => sum + (aff.paidCount || 0), 0)
  const totalVerifiedUsers = downlines.reduce((sum, aff) => sum + (aff.verifiedCount || 0), 0)
  const totalDownlineUsers = downlines.reduce((sum, aff) => sum + (aff.downlineCount || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#7cff01]" />
      </div>
    )
  }

  if (!stats) return <p className="text-slate-400 text-center py-10">Failed to load affiliate stats.</p>

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black text-white">Affiliate Rewards</h2>
        <p className="mt-1 text-sm text-slate-400">Track referrals, subscriptions, and withdrawals.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-500/10 text-purple-400">
              <Users size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Referrers</p>
              <p className="text-2xl font-black text-white">{downlines.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400">
              <Users size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Referred</p>
              <p className="text-2xl font-black text-white">{totalDownlineUsers}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-green-500/10 text-green-400">
              <BadgeCheck size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Verified</p>
              <p className="text-2xl font-black text-white">{totalVerifiedUsers}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-yellow-500/10 text-yellow-400">
              <Crown size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Paid (Pro)</p>
              <p className="text-2xl font-black text-white">{totalPaidUsers}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-green-500/10 text-green-400">
              <Wallet size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Tokens Paid</p>
              <p className="text-2xl font-black text-white">{stats.totalTokensPaid}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-400">
              <DollarSign size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Naira Paid</p>
              <p className="text-2xl font-black text-white">₦{stats.totalNairaPaid.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#07141a] rounded-2xl p-1 border border-white/10">
        {[
          { id: 'tracking' as const, label: 'Referral Tracking', icon: Users },
          { id: 'wallets' as const, label: 'Wallets', icon: Wallet },
          { id: 'withdrawals' as const, label: 'Withdrawals', icon: DollarSign },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-[#7cff01] text-black'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.id === 'withdrawals' && stats.pendingWithdrawals.length > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                {stats.pendingWithdrawals.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Referral Tracking Tab */}
      {activeTab === 'tracking' && (
        <div className="space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or referral code..."
              className="w-full pl-10 pr-4 py-3 rounded-2xl bg-[#07141a] border border-white/10 text-white placeholder-gray-500 text-sm focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
            />
          </div>

          {loadingDownlines ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[#7cff01]" />
            </div>
          ) : filteredAffiliates.length === 0 ? (
            <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-12 text-center">
              <Users className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">{searchQuery ? 'No results found' : 'No affiliates yet'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAffiliates.map((aff: any) => (
                <div key={aff.id} className="rounded-[24px] border border-white/10 bg-[#07141a] overflow-hidden">
                  {/* Referrer Header */}
                  <button
                    onClick={() => setExpandedAff(expandedAff === aff.id ? null : aff.id)}
                    className="w-full p-5 flex items-center justify-between hover:bg-white/[0.02] transition text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-[#7cff01]/10 flex items-center justify-center text-[#7cff01] font-black text-lg">
                        {(aff.full_name || aff.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{aff.full_name || 'Unknown'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail size={10} className="text-gray-500" />
                          <p className="text-xs text-slate-500">{aff.email}</p>
                          <span className="text-slate-700">|</span>
                          <p className="text-xs text-slate-500">Code: <span className="text-[#7cff01] font-bold">{aff.referral_code}</span></p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:flex items-center gap-3">
                        <span className="px-3 py-1 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-black">
                          {aff.downlineCount || 0} Referrals
                        </span>
                        <span className="px-3 py-1 rounded-xl bg-green-500/10 text-green-400 text-xs font-black">
                          {aff.paidCount || 0} Paid
                        </span>
                        <span className="px-3 py-1 rounded-xl bg-purple-500/10 text-purple-400 text-xs font-black">
                          {aff.verifiedCount || 0} Verified
                        </span>
                      </div>
                      {expandedAff === aff.id ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                    </div>
                  </button>

                  {/* Expanded Downlines */}
                  {expandedAff === aff.id && aff.downlines && aff.downlines.length > 0 && (
                    <div className="border-t border-white/5 bg-black/20 p-4">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Name</th>
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Email</th>
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Role</th>
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Status</th>
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Subscription</th>
                            <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aff.downlines.map((d: any) => (
                            <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                              <td className="py-2.5 text-white font-medium">{d.full_name || 'Unknown'}</td>
                              <td className="py-2.5 text-slate-400">{d.email}</td>
                              <td className="py-2.5">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                  d.role === 'scout' ? 'bg-blue-500/10 text-blue-400' :
                                  d.role === 'club' ? 'bg-purple-500/10 text-purple-400' :
                                  'bg-white/5 text-slate-400'
                                }`}>
                                  {d.role || 'player'}
                                </span>
                              </td>
                              <td className="py-2.5">
                                {d.is_verified ? (
                                  <span className="flex items-center gap-1 text-green-400">
                                    <BadgeCheck size={12} /> Verified
                                  </span>
                                ) : (
                                  <span className="text-slate-500">Unverified</span>
                                )}
                              </td>
                              <td className="py-2.5">
                                {d.subscription?.isPro ? (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 text-[10px] font-black uppercase w-fit">
                                    <Crown size={10} /> Pro
                                  </span>
                                ) : d.subscription?.status === 'active' ? (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-black uppercase w-fit">
                                    Active
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-500 text-[10px] font-black uppercase w-fit">
                                    Free
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {expandedAff === aff.id && (!aff.downlines || aff.downlines.length === 0) && (
                    <div className="border-t border-white/5 bg-black/20 p-6 text-center">
                      <p className="text-gray-500 text-xs">No referrals yet</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wallets Tab */}
      {activeTab === 'wallets' && (
        <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
          <h3 className="text-lg font-black text-white">All Affiliate Wallets</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="pb-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">User</th>
                  <th className="pb-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Email</th>
                  <th className="pb-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Balance</th>
                  <th className="pb-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Earned</th>
                  <th className="pb-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Withdrawn</th>
                </tr>
              </thead>
              <tbody>
                {stats.wallets.map((w: any) => (
                  <tr key={w.id} className="border-b border-white/5">
                    <td className="py-3 text-white">{w.profiles?.full_name || w.user_id}</td>
                    <td className="py-3 text-slate-400">{w.profiles?.email || '—'}</td>
                    <td className="py-3 text-[#7cff01] font-black">{w.token_balance}</td>
                    <td className="py-3 text-slate-300">{w.total_earned}</td>
                    <td className="py-3 text-slate-300">{w.total_withdrawn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Withdrawals Tab */}
      {activeTab === 'withdrawals' && (
        <div className="space-y-6">
          <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
            <h3 className="text-lg font-black text-white">Pending Withdrawals</h3>
            {stats.pendingWithdrawals.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No pending withdrawal requests.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {stats.pendingWithdrawals.map((w) => (
                  <div key={w.id} className="rounded-2xl border border-white/5 bg-black/20 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-white">
                          {w.profiles?.full_name || 'Unknown'} — {w.tokens} tokens (₦{w.amount_naira.toLocaleString()})
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{w.profiles?.email}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {w.bank_name} | {w.account_number} | {w.account_name}
                        </p>
                        <p className="text-xs text-slate-500">WhatsApp: {w.whatsapp_number}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => processWithdrawal(w.id, 'paid')}
                          disabled={processingId === w.id}
                          className="flex items-center gap-1 rounded-xl bg-[#7cff01] px-3 py-2 text-xs font-black text-black hover:bg-[#8eff1a] disabled:opacity-50"
                        >
                          <CheckCircle2 size={14} /> Mark Paid
                        </button>
                        <button
                          onClick={() => processWithdrawal(w.id, 'rejected')}
                          disabled={processingId === w.id}
                          className="flex items-center gap-1 rounded-xl bg-red-600 px-3 py-2 text-xs font-black text-white hover:bg-red-500 disabled:opacity-50"
                        >
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminAffiliates
